import * as React from 'react';

// API
import apibridge from '../apibridge';

// UTILS
import { useUpdateTotalNotification } from '../utils/useUpdateTotalNotification';

// REDUX
import { useAppDispatch, useAppSelector } from '../store';
import UserSlice from '../store/slices/User';

// PACKAGES
import { Link, useLocation } from 'react-router-dom';

// UTILS
import { listenForOutsideClicks, validationErrorsToString } from '../utils';

// ICON
import { ReactComponent as BellIcon } from '../assets/img/bell.svg';
import { ReactComponent as ArrowDownIcon } from '../assets/svg/arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from '../assets/svg/arrow-up.svg';
import { ReactComponent as SettingsIcon } from '../assets/svg/settings-black.svg';
import { ReactComponent as LogOutIcon } from '../assets/svg/logout.svg';

// COMPONENTS
import CircleImage from './CircleImage';
import Button from '../components/Button';

// TYPES
import { NavigationMenuAccessRightsMenuPermissions } from '../api/models/navigation-menu-access-rights-menu-permissions';

interface MainMenuTypes {
	isBurgerMenuShow: boolean;
	setIsBurgerMenuShow: React.Dispatch<React.SetStateAction<boolean>>;
}

const MainMenu: React.FC<MainMenuTypes> = ({ isBurgerMenuShow, setIsBurgerMenuShow }) => {
	const dispatch = useAppDispatch();

	// Get user info from Redux Store
	const user = useAppSelector(({ user }) => user);
	const profileImage = user.profileImage;

	// Get total Notification Count from Redux store
	const totalNotificationCount = useAppSelector((state) => state.notificationCount.totalCount);

	// Check if Organisation is active
	const isActiveOrganisation = useAppSelector((state) => state.user.isOrganisationActive);

	// Error state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [mainMenuItems, setMainMenuItems] = React.useState<NavigationMenuAccessRightsMenuPermissions>({});

	// State to control Account Menu
	const [isAccountMenuOpen, setIsAccountMenuOpen] = React.useState<boolean>(false);
	const menuRef = React.useRef(null);
	const menuToggle = () => setIsAccountMenuOpen(!isAccountMenuOpen);

	listenForOutsideClicks(menuRef, isAccountMenuOpen, setIsAccountMenuOpen);

	const doLogout = () => {
		dispatch(UserSlice.actions.logOut());
	};

	const getMainMenu = async () => {
		const response = await apibridge.getMainMenu();

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length ? validationErrorsToString(response.validationErrors) : response.message
			);
		} else if (response.result) {
			setMainMenuItems(response.result);
		}
	};

	React.useEffect(() => {
		useUpdateTotalNotification();
	}, [user.id]);

	// Load data from api
	React.useEffect(() => {
		getMainMenu();
	}, []);

	const {
		dashboard,

		staff,
		leadershipTeam,
		families,
		visitors,
		requestsToJoin,
		inviteToJoins,

		childSafetyStandards,
		policies,
		training,
		safetyChecklists,

		feedback,
		incidentReports,
		complaints,

		audit,
		settings,
		helpDesk,

		companies,
		trainingModules,
		policyTemplates,
		checklistTemplates,
		complaintsAdvice,
		childSafetyTemplates,
		helpDeskTemplates
	} = mainMenuItems;

	const hasPeopleLinks = () => {
		return !![staff, leadershipTeam, families, visitors, requestsToJoin, inviteToJoins].filter(
			(link) => link && link.canRead
		).length;
	};

	const hasResourcesLinks = () => {
		return !![childSafetyStandards, policies, training, safetyChecklists].filter((link) => link && link.canRead).length;
	};

	const hasIssuesLinks = () => {
		return !![feedback, incidentReports, complaints].filter((link) => link && link.canRead).length;
	};

	const hasContentLinks = () => {
		return !![trainingModules, policyTemplates, checklistTemplates, complaintsAdvice, childSafetyTemplates].filter(
			(link) => link && link.canRead
		).length;
	};

	// Detect pathname on the url to load pages accordingly
	const { pathname } = useLocation<string>();

	// Close menu on mobile when clicking on a sidebar link
	const mobileMenuHandler = () => {
		setIsBurgerMenuShow(!isBurgerMenuShow);
	};

	// Close mobile account menu
	const closeMobileAccountMenu = () => {
		setIsAccountMenuOpen(!isAccountMenuOpen);

		setIsBurgerMenuShow(!isBurgerMenuShow);
	};

	return (
		<nav className={`main-menu ${isBurgerMenuShow ? 'show' : ''}`}>
			<h1 className="sr-only">Main Menu</h1>

			{dashboard && dashboard.canRead && (
				<section className={`menu-summary-wrapper ${pathname === '/dashboard' ? 'active' : ''}`}>
					<Link className="menu-link" onClick={mobileMenuHandler} to="/dashboard">
						Dashboard
					</Link>

					<div className="noti-group">
						<BellIcon className="noti-icon" />

						{totalNotificationCount && totalNotificationCount > 0 ? (
							<span className="noti-total">{totalNotificationCount}</span>
						) : null}
					</div>
				</section>
			)}

			{hasPeopleLinks() && (
				<section className="nav-section">
					<h2 className="main-menu-heading">People</h2>

					<ul>
						{/* STAFF */}
						{staff && staff.canRead && (
							<li>
								<Link
									className={`menu-link ${pathname.indexOf('/staff') === 0 ? 'active' : ''} 
									}`}
									onClick={mobileMenuHandler}
									to="/staff"
								>
									<span className="menu-link-name">Staff & Volunteers</span>
								</Link>
							</li>
						)}

						{/* LEADERSHIP TEAM */}
						{leadershipTeam && leadershipTeam.canRead && (
							<li>
								<Link
									className={`menu-link ${pathname.indexOf('/leadershipteam') === 0 ? 'active' : ''} 
									}`}
									onClick={mobileMenuHandler}
									to="/leadershipteam"
								>
									<span className="menu-link-name">Leadership Team</span>
								</Link>
							</li>
						)}

						{/* FAMILIES */}
						{families && families.canRead && (
							<li>
								<Link
									className={`menu-link ${pathname.indexOf('/families') === 0 ? 'active' : ''} 
									}`}
									onClick={mobileMenuHandler}
									to="/families"
								>
									<span className="menu-link-name">Families</span>
								</Link>
							</li>
						)}

						{/* VISITORS */}
						{visitors && visitors.canRead && (
							<li>
								<Link
									className={`menu-link ${pathname.indexOf('/visitors') === 0 ? 'active' : ''}`}
									onClick={mobileMenuHandler}
									to="/visitors"
								>
									<span className="menu-link-name">Visitors</span>
								</Link>
							</li>
						)}

						{/* REQUESTS TO JOIN */}
						{requestsToJoin && requestsToJoin.canRead && (
							<li>
								<Link
									className={`menu-link ${pathname.indexOf('/requests-to-join') === 0 ? 'active' : ''}`}
									onClick={mobileMenuHandler}
									to="/requests-to-join"
								>
									<span className="menu-link-name">Requests to join</span>
								</Link>
							</li>
						)}

						{/* INVITE TO JOIN */}
						{inviteToJoins && inviteToJoins.canRead && (
							<li>
								<Link
									className={`menu-link ${pathname === '/invite-to-join' ? 'active' : ''}`}
									onClick={mobileMenuHandler}
									to="/invite-to-join"
								>
									<span className="menu-link-name">Invite to join</span>
								</Link>
							</li>
						)}
					</ul>
				</section>
			)}

			{hasResourcesLinks() && (
				<section className="nav-section">
					<h2 className="main-menu-heading">Resources</h2>

					<ul>
						{/* CHILD SAFE STANDARDS */}
						{childSafetyStandards && childSafetyStandards.canRead && (
							<li>
								<Link
									className={`menu-link ${pathname.indexOf('/child-safety-standards') === 0 ? 'active' : ''}`}
									onClick={mobileMenuHandler}
									to="/child-safety-standards"
								>
									<span className="menu-link-name">Child Safe Standards</span>
								</Link>
							</li>
						)}

						{/* POLICIES */}
						{policies && policies.canRead && (
							<li>
								<Link
									className={`menu-link ${
										pathname.indexOf('/policies') === 0 ||
										pathname.indexOf('/policy-details') === 0 ||
										pathname.indexOf('/policy-templates') === 0 ||
										pathname.indexOf('/policy-submit') === 0
											? 'active'
											: ''
									} 
								}`}
									onClick={mobileMenuHandler}
									to="/policies"
								>
									<span className="menu-link-name">Policies</span>
								</Link>
							</li>
						)}

						{/* TRAINING */}
						{training && training.canRead && (
							<li>
								<Link
									className={`menu-link ${pathname.indexOf('/training') === 0 ? 'active' : ''} 
									}`}
									onClick={mobileMenuHandler}
									to="/training"
								>
									<span className="menu-link-name">Training</span>
								</Link>
							</li>
						)}

						{/* SAFETY CHECKLISTS */}
						{safetyChecklists && safetyChecklists.canRead && (
							<li>
								<Link
									className={`menu-link ${
										pathname.indexOf('/safety-checklists') === 0 ||
										pathname.indexOf('/new-checklist') === 0 ||
										pathname.indexOf('/preview-checklist') === 0 ||
										pathname.indexOf('/create-checklist') === 0
											? 'active'
											: ''
									} 
									}`}
									onClick={mobileMenuHandler}
									to="/safety-checklists"
								>
									<span className="menu-link-name">Safety Checklists</span>
								</Link>
							</li>
						)}
					</ul>
				</section>
			)}

			{hasIssuesLinks() && (
				<section className="nav-section">
					<h2 className="main-menu-heading">Issues</h2>

					<ul>
						{/* FEEDBACK */}
						{feedback && feedback.canRead && (
							<li>
								<Link
									className={`menu-link ${
										pathname.indexOf('/feedback') === 0 || pathname.indexOf('/feedback-submit') === 0 ? 'active' : ''
									} 
								}`}
									onClick={mobileMenuHandler}
									to="/feedback"
								>
									<span className="menu-link-name">Feedback</span>
								</Link>
							</li>
						)}

						{/* COMPLAINTS */}
						{complaints && complaints.canRead && (
							<li>
								<Link
									className={`menu-link ${
										pathname.indexOf('/complaints') === 0 ||
										pathname.indexOf('/complaints-advice-templates') === 0 ||
										pathname.indexOf('/complaints-submit') === 0
											? 'active'
											: ''
									} 
								}`}
									onClick={mobileMenuHandler}
									to="/complaints"
								>
									<span className="menu-link-name">Complaints</span>
								</Link>
							</li>
						)}

						{/* INCIDENT REPORTS */}
						{incidentReports && incidentReports.canRead && (
							<li>
								<Link
									className={`menu-link ${
										pathname.indexOf('/incident-reports') === 0 || pathname.indexOf('/incident-submit') === 0
											? 'active'
											: ''
									} 
								}`}
									onClick={mobileMenuHandler}
									to="/incident-reports"
								>
									<span className="menu-link-name">Incident reports</span>
								</Link>
							</li>
						)}
					</ul>
				</section>
			)}

			{/* AUDIT */}
			{audit && audit.canRead && (
				<section className="main-menu-section">
					<Link
						className={`menu-link highlight ${pathname === '/audit' ? 'active' : ''}`}
						onClick={mobileMenuHandler}
						to="/audit"
					>
						<span className="menu-link-name">Audit</span>
					</Link>
				</section>
			)}

			{/* SETTINGS */}
			{settings && settings.canRead && (
				<section className="main-menu-section">
					<Link
						className={`menu-link highlight ${pathname === '/settings' ? 'active' : ''}`}
						onClick={mobileMenuHandler}
						to="/settings"
					>
						<span className="menu-link-name">Settings</span>
					</Link>
				</section>
			)}

			{helpDesk && helpDesk.canRead && (
				<section className="main-menu-section">
					<Link
						className={`menu-link highlight ${pathname === '/help-desk' ? 'active' : ''}`}
						onClick={mobileMenuHandler}
						to="/help-desk"
					>
						<span className="menu-link-name">Help Desk</span>
					</Link>
				</section>
			)}

			{/* COMPANIES */}
			{companies && companies.canRead && (
				<section className="main-menu-section">
					<Link
						className={`menu-link highlight ${pathname.indexOf('/companies') === 0 ? 'active' : ''}`}
						onClick={mobileMenuHandler}
						to="/companies"
					>
						<span className="menu-link-name">Companies</span>
					</Link>
				</section>
			)}

			{hasContentLinks() && (
				<section className="nav-section mtop-24">
					<h2 className="main-menu-heading">Content</h2>

					<ul>
						{/* TRAINING MODULES */}
						{trainingModules && trainingModules.canRead && (
							<li>
								<Link
									className={`menu-link ${
										pathname.indexOf('/content-training-module') === 0 ||
										pathname.indexOf('/content-training-module-edit') === 0
											? 'active'
											: ''
									} 
								}`}
									onClick={mobileMenuHandler}
									to="/content-training-module"
								>
									<span className="menu-link-name">Training Modules</span>
								</Link>
							</li>
						)}

						{/* POLICY TEMPLATES */}
						{policyTemplates && policyTemplates.canRead && (
							<li>
								<Link
									className={`menu-link ${
										pathname.indexOf('/content-policy-templates') === 0 ||
										pathname.indexOf('/content-policy-templates-edit') === 0
											? 'active'
											: ''
									} 
								}`}
									onClick={mobileMenuHandler}
									to="/content-policy-templates"
								>
									<span className="menu-link-name">Policy Templates</span>
								</Link>
							</li>
						)}

						{/* CHECKLIST TEAMPLATES */}
						{checklistTemplates && checklistTemplates.canRead && (
							<li>
								<Link
									className={`menu-link ${
										pathname.indexOf('/content-checklist-templates') === 0 ||
										pathname.indexOf('/content-checklist-templates-edit') === 0
											? 'active'
											: ''
									} 
								}`}
									onClick={mobileMenuHandler}
									to="/content-checklist-templates"
								>
									<span className="menu-link-name">Checklist Templates</span>
								</Link>
							</li>
						)}

						{/* COMPLAINTS ADVICE */}
						{complaintsAdvice && complaintsAdvice.canRead && (
							<li>
								<Link
									className={`menu-link ${
										pathname.indexOf('/content-complaints-advice') === 0 ||
										pathname.indexOf('/content-complaints-advice-edit') === 0
											? 'active'
											: ''
									} 
								}`}
									onClick={mobileMenuHandler}
									to="/content-complaints-advice"
								>
									<span className="menu-link-name">Complaints Advice</span>
								</Link>
							</li>
						)}

						{/* CHILD SAFE STANDARDS */}
						{childSafetyTemplates && childSafetyTemplates.canRead && (
							<li>
								<Link
									className={`menu-link ${
										pathname.indexOf('/content-child-safe-standards') === 0 ||
										pathname.indexOf('/content-child-safe-edit') === 0
											? 'active'
											: ''
									} 
								}`}
									onClick={mobileMenuHandler}
									to="/content-child-safe-standards"
								>
									<span className="menu-link-name">Child Safe Standards</span>
								</Link>
							</li>
						)}

						{/* CONTENT HELP DESK */}
						{helpDeskTemplates && helpDeskTemplates.canRead && (
							<li>
								<Link
									className={`menu-link ${pathname.indexOf('/content-help-desk') === 0 ? 'active' : ''} 
								}`}
									onClick={mobileMenuHandler}
									to="/content-help-desk"
								>
									<span className="menu-link-name">Help Desk</span>
								</Link>
							</li>
						)}
					</ul>
				</section>
			)}

			{/* My Account for Mobile */}
			{isBurgerMenuShow && (
				<section className="main-menu-section mobile">
					<div className="mobile-account-menu">
						<div className="account-info-container row" onClick={menuToggle}>
							<div className="col-8 account-info">
								<CircleImage
									isColorBorder={true}
									src={profileImage}
									alt="profile picture"
									size={40}
									firstName={user.firstName}
									lastName={user.lastName}
								/>

								<h4 className="account-name">
									{user.firstName} {user.lastName}
								</h4>
							</div>

							<button type="button" ref={menuRef} className="dropdown-wrapper">
								{isAccountMenuOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
							</button>
						</div>

						{isAccountMenuOpen && (
							<div className={`my-account-menu ${isAccountMenuOpen ? 'show' : ''} `}>
								{isActiveOrganisation && (
									<Link className="my-account-menu-item" onClick={closeMobileAccountMenu} to="/my-account">
										<span className="my-account-icon-container">
											<SettingsIcon />
										</span>
										<span>My Account</span>
									</Link>
								)}

								<Button className="my-account-menu-item" onClick={doLogout}>
									<span className="my-account-icon-container">
										<LogOutIcon />
									</span>
									<span>Logout</span>
								</Button>
							</div>
						)}
					</div>
				</section>
			)}
		</nav>
	);
};

export default MainMenu;
